import React, { useState, useEffect, useContext, JSX } from 'react';
import {
  Container,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Paper,
  Box,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import PermissionsModal from '@/components/modals/permissions/PermissionsModal';
import ArchivedChat from './modals/ArchivedChat';
import LoadingData from '@/components/loading/LoadingData';
import Loading from '@/components/loading/Loading';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { useLoading } from '@/context_providers/loading_context/LoadingContext';
import { UserContext } from '@/context_providers/user_context/UserProvider';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import { getAllEmergencyByID } from '@/repository/EmergencyDAO';

import { Link as DownloadLink } from '@mui/icons-material';
import { EmergencyHistoryReport } from '@/reports/EmergencyHistoryReport/EmergencyHistoryReport';
import { downloadPDF } from '@/reports/utils/ReportUtils';
import { useTranslation } from 'react-i18next';
import { ErrorStringKeys, StringKeys } from '@/resources/strings/StringKeys';
import COLORS from '@/resources/style/Themes';
import { getLocationsBetweenTimestamps } from '@/repository/LocationDAO';
import {
  EmergencyFullData,
  LocationQueryByTimeRequest,
  UserHistoryObservedEmergencyModel,
} from '../../../../generated-client/models';

export const EmergencyHistory = () => {
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const { orgUser, userHistory, userPrivateSettings } =
    useUserUpdaterProvider();
  const [selectedEmergency, setSelectedEmergency] =
    useState<EmergencyFullData | null>(null);
  const [showEmergencyChat, setShowEmergencyChat] = useState(false);
  const { setPageLoading } = useLoading();
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const { showErrorToast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (orgUser && userPrivateSettings) {
      setPageLoading(false);
    }
  }, [orgUser, userPrivateSettings]);

  const handleOpenChat = async (key: string) => {
    try {
      setLoadingAPIData(true);
      const emergency = await getAllEmergencyByID(key, orgUser?.org_code ?? '');

      setSelectedEmergency(emergency);
      setShowEmergencyChat(true);
    } catch (error) {
      showErrorToast(t(ErrorStringKeys.FAILED_TO_RETRIEVE_EMERGENCY));
    } finally {
      setLoadingAPIData(false);
    }
  };

  const generatePDF = async (key: string) => {
    try {
      setLoadingAPIData(true);
      const allEmergencyData = await getAllEmergencyByID(
        key,
        orgUser?.org_code ?? '',
      );

      const emergency = allEmergencyData.emergency;
      const emergencyMessages = allEmergencyData.emergency_messages;
      const emergencyLocations = allEmergencyData.locations;

      if (!emergency) {
        showErrorToast(t(ErrorStringKeys.FAILED_TO_GENERATE_REPORT));
        return;
      }
      const auth_ids = new Set<string>();
      if (emergency.creator_auth_id) {
        auth_ids.add(emergency.creator_auth_id);
      }

      if (Array.isArray(emergency.users)) {
        emergency.users.forEach((user) => {
          if (user.auth_id) {
            auth_ids.add(user.auth_id);
          }
        });
      }

      if (orgUser) {
        auth_ids.add(orgUser.auth_id);
      }

      const requestPayload: LocationQueryByTimeRequest = {
        auth_ids: Array.from(auth_ids),
        start_timestamp: emergency.creation_timestamp,
        end_timestamp: emergency.resolved_timestamp ?? Date.now(),
      };

      let locationsByUserId = {};

      try {
        locationsByUserId = await getLocationsBetweenTimestamps(requestPayload);
      } catch (e) {
        showErrorToast(t(ErrorStringKeys.FAILED_TO_GENERATE_REPORT));
        return;
      }

      let doc: JSX.Element;
      try {
        doc = await EmergencyHistoryReport({
          emergency,
          emergencyLocations,
          emergencyMessages,
          orgUser,
          locationsByUserId,
          t,
        });
      } catch (err) {
        showErrorToast('PDF Generation failed at rendering stage.');
        return;
      }
      downloadPDF(
        doc,
        `${emergency.incident} - ${emergency.creator_first_name} ${emergency.creator_last_name}`,
      );
    } catch (error) {
      showErrorToast(t(ErrorStringKeys.FAILED_TO_GENERATE_REPORT));
    } finally {
      setLoadingAPIData(false);
    }
  };

  const sortedEmergencies = React.useMemo(() => {
    if (userHistory && userHistory.observed_emergencies) {
      return Object.entries(userHistory.observed_emergencies).sort(
        (a, b) => b[1].creation_timestamp - a[1].creation_timestamp,
      );
    }
    return [];
  }, [userHistory]);

  const renderData = () => (
    <>
      <Box sx={{ mt: 2 }}>
        <div
          className="d-flex justify-content-between align-items-center mb-3"
          style={{ marginTop: '20px' }}
        >
          <Typography variant="h5" sx={{ mt: 2 }}>
            {t(StringKeys.EMERGENCY_HISTORY)}
          </Typography>
        </div>
      </Box>
      {sortedEmergencies.length > 0 ? (
        renderNestedData(sortedEmergencies)
      ) : (
        <Typography>{t(StringKeys.YOUR_EMERGENCY_HISTORY_IS_EMPTY)}</Typography>
      )}
    </>
  );

  const renderNestedData = (
    sortedEmergencies: [string, UserHistoryObservedEmergencyModel][],
  ) => (
    <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
            >
              {t(StringKeys.DATE)}
            </TableCell>
            <TableCell
              sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
            >
              {t(StringKeys.INCIDENT)}
            </TableCell>
            <TableCell
              sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
            >
              {t(StringKeys.EMERGENCY_INITIATOR)}
            </TableCell>
            <TableCell
              sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
            >
              {t(StringKeys.RESOLVED)}
            </TableCell>
            <TableCell
              sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
              align="center"
            >
              {t(StringKeys.DOWNLOAD)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEmergencies.map(([key, element]) => {
            const date = new Date(element.creation_timestamp);
            const formattedDate = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(
              date.getDate(),
            ).padStart(2, '0')}/${date.getFullYear()}`;

            return (
              <TableRow key={key} hover style={{ cursor: 'pointer' }}>
                <TableCell onClick={() => handleOpenChat(key)}>
                  {formattedDate}
                </TableCell>
                <TableCell onClick={() => handleOpenChat(key)}>
                  {element.incident}
                </TableCell>
                <TableCell
                  onClick={() => handleOpenChat(key)}
                >{`${element.creator_first_name} ${element.creator_last_name}`}</TableCell>
                <TableCell onClick={() => handleOpenChat(key)}>
                  {element.resolved_timestamp
                    ? t(StringKeys.YES)
                    : t(StringKeys.NO)}
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => generatePDF(key)}>
                    <DownloadLink />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl">
      {orgUser && userPrivateSettings && userHistory ? (
        renderData()
      ) : (
        <Loading loadingText={t(StringKeys.LOADING)} />
      )}
      <PermissionsModal
        show={showPermissionsModal}
        onHide={() => setShowPermissionsModal(false)}
      />
      <ArchivedChat
        show={showEmergencyChat}
        onHide={() => {
          setSelectedEmergency(null);
          setShowEmergencyChat(false);
        }}
        selectedEmergency={selectedEmergency}
      />
      <LoadingData show={loadingAPIData} />
    </Container>
  );
};

export default withAuthenticationRequired(EmergencyHistory, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
